import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// English resource bundles (source of truth). Other languages mirror this
// structure under ./locales/<lang>/. See ./README.md for the key convention.
import enActivity from './locales/en/activity.json';
import enAddPatient from './locales/en/addPatient.json';
import enAdmin from './locales/en/admin.json';
import enAlertDetail from './locales/en/alertDetail.json';
import enAuth from './locales/en/auth.json';
import enCards from './locales/en/cards.json';
import enCommon from './locales/en/common.json';
import enCompliance from './locales/en/compliance.json';
import enDailyMetrics from './locales/en/dailyMetrics.json';
import enDailyParameters from './locales/en/dailyParameters.json';
import enMonitoring from './locales/en/monitoring.json';
import enNavigation from './locales/en/navigation.json';
import enPatientDetail from './locales/en/patientDetail.json';
import enPatients from './locales/en/patients.json';
import enSettings from './locales/en/settings.json';
import enSupport from './locales/en/support.json';
import enTests from './locales/en/tests.json';

// German / French stubs. These are intentionally partial for now — any key
// missing here falls back to English via `fallbackLng`.
import deAdmin from './locales/de/admin.json';
import deCommon from './locales/de/common.json';
import dePatients from './locales/de/patients.json';
import frAdmin from './locales/fr/admin.json';
import frCommon from './locales/fr/common.json';
import frPatients from './locales/fr/patients.json';

export const defaultNS = 'common';

// Namespaces are split per feature. Register new ones here as they are added.
export const resources = {
  en: {
    common: enCommon,
    admin: enAdmin,
    patients: enPatients,
    navigation: enNavigation,
    support: enSupport,
    settings: enSettings,
    dailyMetrics: enDailyMetrics,
    compliance: enCompliance,
    tests: enTests,
    patientDetail: enPatientDetail,
    monitoring: enMonitoring,
    alertDetail: enAlertDetail,
    activity: enActivity,
    addPatient: enAddPatient,
    dailyParameters: enDailyParameters,
    cards: enCards,
    auth: enAuth,
  },
  // German / French only carry the namespaces that have been translated so far.
  // Any namespace (or key) missing here resolves to English via `fallbackLng`,
  // so newly extracted namespaces are registered under `en` only until a
  // translated `de`/`fr` file is added.
  de: { common: deCommon, admin: deAdmin, patients: dePatients },
  fr: { common: frCommon, admin: frAdmin, patients: frPatients },
} as const;

// Languages the product is being localised for. Drives the language switcher
// and MUI locale selection.
export const supportedLngs = ['en', 'de', 'fr'] as const;
export type SupportedLanguage = (typeof supportedLngs)[number];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: supportedLngs as unknown as string[],
    defaultNS,
    ns: [
      'common',
      'admin',
      'patients',
      'navigation',
      'support',
      'settings',
      'dailyMetrics',
      'compliance',
      'tests',
      'patientDetail',
      'monitoring',
      'alertDetail',
      'activity',
      'addPatient',
      'dailyParameters',
      'cards',
      'auth',
    ],
    interpolation: {
      // React already escapes values, so i18next must not double-escape.
      escapeValue: false,
    },
    react: {
      // Resources are bundled synchronously, so there is nothing to suspend on.
      // Disabling Suspense avoids needing a boundary (and simplifies tests).
      useSuspense: false,
    },
    detection: {
      // Prefer an explicit stored choice, then the browser language.
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
  });

export default i18n;
